import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import axios from "axios";
import { ArrowRight, Building2, Check, Eye, EyeOff, LockKeyhole, Mail, UserRound, Users } from "lucide-react";
import Maintopbar from "./maintopbar.tsx";
import PublicFooter from "./PublicFooter.tsx";
import "./CSS/PublicAuth.css";

interface FormData {
  firstName: string;
  lastName: string;
  companyName: string;
  email: string;
  password: string;
  isadmin: boolean;
}

const RegistrationForm: React.FC = () => {
  const [formData, setFormData] = useState<FormData>({ firstName: "", lastName: "", companyName: "", email: "", password: "", isadmin: true });
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [birthDate, setBirthDate] = useState("");
  const [ageConfirmed, setAgeConfirmed] = useState(false);
  const navigate = useNavigate();

  const updateField = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setFormData((current) => ({ ...current, [name]: value }));
  };

  const checkAge = (event: React.FormEvent) => {
    event.preventDefault();
    const parts = birthDate.split("-").map(Number);
    if (parts.length !== 3 || parts.some((part) => !Number.isInteger(part))) {
      setErrorMessage("Enter your date of birth.");
      return;
    }
    const [year, month, day] = parts;
    const today = new Date();
    const parsedBirthDate = new Date(year, month - 1, day);
    const adultDay = Math.min(day, new Date(year + 18, month, 0).getDate());
    const eighteenthBirthday = new Date(year + 18, month - 1, adultDay);
    if (year < 1900 || parsedBirthDate.getFullYear() !== year ||
        parsedBirthDate.getMonth() !== month - 1 ||
        parsedBirthDate.getDate() !== day || eighteenthBirthday > today) {
      setErrorMessage("You must be at least 18 years old to create an account.");
      return;
    }
    setErrorMessage("");
    setAgeConfirmed(true);
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (submitting || !ageConfirmed) return;
    setErrorMessage("");
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
      setErrorMessage("Enter a valid email address.");
      return;
    }
    if (formData.password.length < 8 || !/[^A-Za-z0-9]/.test(formData.password)) {
      setErrorMessage("Use at least 8 characters and one special character for your password.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post("https://timetrakerapi.sntservices.in/register", {
        ...formData,
        birthDate,
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        companyName: formData.companyName.trim(),
        email: formData.email.trim(),
      });
      toast.success("Account created. You can now log in.");
      navigate("/login");
    } catch (error: any) {
      const serverMessage = error.response?.data;
      setErrorMessage(typeof serverMessage === "string" ? serverMessage : serverMessage?.message || "Unable to create your account. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="public-auth-page">
      <Maintopbar />
      <main className="public-auth-main public-auth-register">
        <div className="public-auth-shell">
          <aside className="public-auth-story"><span className="public-auth-kicker">GET STARTED</span><h1>Bring your team's work into focus.</h1><p>Build a clearer picture of time, projects, and progress with a workspace designed for managers and staff.</p><div className="public-auth-story-list"><span><Check size={17} /> Organize clients, projects, and tasks</span><span><Check size={17} /> Track time from the desktop</span><span><Check size={17} /> Review timelines and reports</span></div><div className="public-auth-story-decor" aria-hidden="true"><span /><span /><span /><span /><span /></div></aside>
          <div className="public-auth-panel public-auth-panel-register"><div className="public-auth-panel-head"><span className="public-auth-kicker">CREATE YOUR ACCOUNT</span><h2>{ageConfirmed ? "Let's get started" : "Check eligibility"}</h2><p>{ageConfirmed ? "Enter your details and choose how you'll use the workspace." : "This workplace service is for adults aged 18 or older. Enter your date of birth before providing account details. We use it only to check eligibility and do not save it."}</p></div>
            {!ageConfirmed ? <form onSubmit={checkAge} className="public-auth-form">
              <div className="public-auth-field"><label htmlFor="register-birth-date">Date of birth</label><div className="public-auth-input-wrap"><input id="register-birth-date" type="date" value={birthDate} onChange={(event) => { setBirthDate(event.target.value); setErrorMessage(""); }} required /></div></div>
              {errorMessage && <p className="public-auth-error" role="alert">{errorMessage}</p>}
              <button type="submit" className="public-auth-submit">Continue <ArrowRight size={18} /></button>
            </form> : <form onSubmit={handleSubmit} className="public-auth-form">
              <fieldset className="public-auth-role"><legend>I am joining as</legend><div className="public-auth-role-options"><label className={formData.isadmin ? "selected" : ""}><input type="radio" name="accountType" checked={formData.isadmin} onChange={() => setFormData((current) => ({ ...current, isadmin: true }))} /><span><Building2 size={19} /><strong>Manager</strong><small>Set up a team</small></span></label><label className={!formData.isadmin ? "selected" : ""}><input type="radio" name="accountType" checked={!formData.isadmin} onChange={() => setFormData((current) => ({ ...current, isadmin: false }))} /><span><Users size={19} /><strong>Team member</strong><small>Join an existing team</small></span></label></div></fieldset>
              <div className="public-auth-two-col"><div className="public-auth-field"><label htmlFor="register-first-name">First name</label><div className="public-auth-input-wrap"><UserRound size={18} /><input id="register-first-name" name="firstName" value={formData.firstName} onChange={updateField} placeholder="First name" autoComplete="given-name" required /></div></div><div className="public-auth-field"><label htmlFor="register-last-name">Last name</label><div className="public-auth-input-wrap"><UserRound size={18} /><input id="register-last-name" name="lastName" value={formData.lastName} onChange={updateField} placeholder="Last name" autoComplete="family-name" required /></div></div></div>
              <div className="public-auth-field"><label htmlFor="register-company">Company name</label><div className="public-auth-input-wrap"><Building2 size={18} /><input id="register-company" name="companyName" value={formData.companyName} onChange={updateField} placeholder="Your company" autoComplete="organization" required /></div></div>
              <div className="public-auth-field"><label htmlFor="register-email">Work email</label><div className="public-auth-input-wrap"><Mail size={18} /><input id="register-email" name="email" type="email" value={formData.email} onChange={updateField} placeholder="you@company.com" autoComplete="email" required /></div></div>
              <div className="public-auth-field"><label htmlFor="register-password">Password</label><div className="public-auth-input-wrap"><LockKeyhole size={18} /><input id="register-password" name="password" type={showPassword ? "text" : "password"} value={formData.password} onChange={updateField} placeholder="Create a password" autoComplete="new-password" minLength={8} required /><button type="button" className="public-auth-reveal" onClick={() => setShowPassword(!showPassword)} aria-label={showPassword ? "Hide password" : "Show password"}>{showPassword ? <EyeOff size={18} /> : <Eye size={18} />}</button></div><small className="public-auth-hint">At least 8 characters, including one special character.</small></div>
              {errorMessage && <p className="public-auth-error" role="alert">{errorMessage}</p>}
              <button type="submit" className="public-auth-submit" disabled={submitting}>{submitting ? "Creating account…" : <>Create account <ArrowRight size={18} /></>}</button>
            </form>}<p className="public-auth-switch">Already have an account? <Link to="/login">Log in</Link></p>
          </div>
        </div>
      </main>
      <PublicFooter />
    </div>
  );
};

export default RegistrationForm;
